import { useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, updateProfile, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { Loader2 } from "lucide-react";
import { auth, authError } from "@/lib/firebase";

export const strongPassword = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);
const actionUrl = () => ({ url: `${window.location.origin}/wallet` });

export const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8Z" /><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24Z" /><path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1Z" /><path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9Z" /></svg>
);

export function GoogleButton({ label = "Continue with Google", onError }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await signInWithPopup(auth, new GoogleAuthProvider()); } catch (e) { onError?.(authError(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={go} disabled={busy} className="flex w-full items-center justify-center gap-3 rounded-2xl border border-ww-border bg-white px-4 py-3 text-[15px] font-semibold text-ww-navy transition-[border-color,box-shadow] duration-200 hover:border-ww-bright hover:shadow-[0_10px_30px_-14px_rgba(8,124,101,0.5)]" data-testid="google-signin-button">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon />}{label}
    </button>
  );
}

export function EmailAuth({ mode }) {
  const [view, setView] = useState(mode === "sign-up" ? "signup" : "signin");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn) => { setErr(""); setInfo(""); setBusy(true); try { await fn(); } catch (e) { setErr(authError(e)); } finally { setBusy(false); } };

  const submit = (e) => {
    e.preventDefault();
    const email = f.email.trim().toLowerCase();
    if (view === "reset") {
      return run(async () => {
        try { await sendPasswordResetEmail(auth, email, actionUrl()); } catch (ex) { if (ex.code !== "auth/user-not-found") throw ex; }
        setInfo("If an account exists for this email, a password reset link is on its way.");
      });
    }
    if (view === "signup") {
      if (!strongPassword(f.password)) return setErr("Use at least 8 characters with letters and numbers.");
      return run(async () => {
        const { user } = await createUserWithEmailAndPassword(auth, email, f.password);
        if (f.name.trim()) await updateProfile(user, { displayName: f.name.trim() });
        await sendEmailVerification(user, actionUrl());
      });
    }
    return run(() => signInWithEmailAndPassword(auth, email, f.password));
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="email-auth">
      {view === "signup" && <input className="ww-input" value={f.name} onChange={set("name")} placeholder="Full Name" maxLength={100} autoComplete="name" data-testid="email-name-input" />}
      <input type="email" required className="ww-input" value={f.email} onChange={set("email")} placeholder="you@email.com" autoComplete="email" data-testid="email-input" />
      {view !== "reset" && <input type="password" required className="ww-input" value={f.password} onChange={set("password")} placeholder={view === "signup" ? "Create a password (8+ characters)" : "Password"} autoComplete={view === "signup" ? "new-password" : "current-password"} data-testid="password-input" />}
      <button className="ww-btn-primary w-full justify-center" disabled={busy} data-testid="email-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{view === "signup" ? "Create Account" : view === "reset" ? "Send Reset Link" : "Sign In"}</button>
      <div className="flex justify-between text-sm">
        {view === "signin" ? <button type="button" onClick={() => setView("reset")} className="font-semibold text-ww-emerald hover:underline" data-testid="forgot-password-link">Forgot password?</button>
          : <button type="button" onClick={() => setView("signin")} className="font-semibold text-ww-emerald hover:underline" data-testid="back-to-signin-link">Back to sign in</button>}
      </div>
      {err && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="email-auth-error">{err}</p>}
      {info && <p className="rounded-xl bg-ww-mint px-3 py-2 text-sm text-ww-emerald" data-testid="email-auth-info">{info}</p>}
    </form>
  );
}
